import { useEffect, useRef, useState } from 'react'
import {
  ArrowRight,
  Code,
  EnvelopeSimple,
  GlobeHemisphereWest,
  Minus,
  PaperPlaneTilt,
  Square,
  X,
} from '@phosphor-icons/react'
import ServicePage from './ServicePages'
import ContactLinks from './ContactLinks'

const matrixGlyphs = '01アイウエオカキクケコサシスセソタチツテトナニヌネノ{}[]<>/\\=+-_*#@$%&?'

function wait(duration) {
  return new Promise((resolve) => window.setTimeout(resolve, duration))
}

function createKeyboardAudio() {
  const AudioContext = window.AudioContext || window.webkitAudioContext
  if (!AudioContext) return null

  const context = new AudioContext({ latencyHint: 'interactive' })
  const frameCount = Math.floor(context.sampleRate * 0.12)
  const noiseBuffer = context.createBuffer(1, frameCount, context.sampleRate)
  const samples = noiseBuffer.getChannelData(0)
  const compressor = context.createDynamicsCompressor()
  const output = context.createGain()

  for (let index = 0; index < frameCount; index += 1) {
    const decay = Math.exp(-index / (frameCount * 0.11))
    const plasticResonance = Math.sin(index * 0.41) * 0.16
    samples[index] = ((Math.random() * 2 - 1) + plasticResonance) * decay
  }

  compressor.threshold.value = -18
  compressor.knee.value = 12
  compressor.ratio.value = 4
  compressor.attack.value = 0.002
  compressor.release.value = 0.08
  output.gain.value = 0.72
  output.connect(compressor)
  compressor.connect(context.destination)

  return { context, noiseBuffer, output }
}

function playKeyboardSound(audio, kind = 'type') {
  if (!audio || audio.context.state !== 'running') return

  const { context, noiseBuffer, output } = audio
  const now = context.currentTime

  const noiseLayer = ({ delay = 0, frequency, type, volume, playbackRate, duration }) => {
    const source = context.createBufferSource()
    const filter = context.createBiquadFilter()
    const gain = context.createGain()
    const start = now + delay

    source.buffer = noiseBuffer
    source.playbackRate.value = playbackRate
    filter.type = type
    filter.frequency.value = frequency
    filter.Q.value = type === 'bandpass' ? 1.25 : 0.72
    gain.gain.setValueAtTime(volume, start)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
    source.connect(filter)
    filter.connect(gain)
    gain.connect(output)
    source.start(start)
    source.stop(start + duration)
  }

  const bodyLayer = ({ frequency, endFrequency, volume, duration }) => {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = 'triangle'
    oscillator.frequency.setValueAtTime(frequency, now)
    oscillator.frequency.exponentialRampToValueAtTime(endFrequency, now + duration)
    gain.gain.setValueAtTime(volume, now)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration)
    oscillator.connect(gain)
    gain.connect(output)
    oscillator.start(now)
    oscillator.stop(now + duration)
  }

  const variation = 0.92 + Math.random() * 0.16

  if (kind === 'enter') {
    noiseLayer({ frequency: 1650, type: 'bandpass', volume: 0.095, playbackRate: 0.82, duration: 0.07 })
    noiseLayer({ delay: 0.006, frequency: 520, type: 'lowpass', volume: 0.062, playbackRate: 0.56, duration: 0.12 })
    noiseLayer({ delay: 0.072, frequency: 2800, type: 'highpass', volume: 0.018, playbackRate: 1.18, duration: 0.035 })
    bodyLayer({ frequency: 108, endFrequency: 54, volume: 0.075, duration: 0.14 })

    const ring = context.createOscillator()
    const ringGain = context.createGain()
    ring.type = 'sine'
    ring.frequency.setValueAtTime(410, now + 0.018)
    ring.frequency.exponentialRampToValueAtTime(330, now + 0.15)
    ringGain.gain.setValueAtTime(0.012, now + 0.018)
    ringGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15)
    ring.connect(ringGain)
    ringGain.connect(output)
    ring.start(now + 0.018)
    ring.stop(now + 0.16)
    return
  }

  const isDelete = kind === 'delete'
  noiseLayer({
    frequency: isDelete ? 2050 : 3150,
    type: 'bandpass',
    volume: isDelete ? 0.052 : 0.058,
    playbackRate: variation * (isDelete ? 0.86 : 1.08),
    duration: 0.038,
  })
  noiseLayer({
    delay: 0.004,
    frequency: isDelete ? 620 : 820,
    type: 'lowpass',
    volume: isDelete ? 0.032 : 0.025,
    playbackRate: variation * 0.7,
    duration: 0.065,
  })
  noiseLayer({
    delay: 0.047 + Math.random() * 0.012,
    frequency: isDelete ? 1850 : 2550,
    type: 'highpass',
    volume: 0.012,
    playbackRate: variation * 1.2,
    duration: 0.024,
  })
  bodyLayer({
    frequency: isDelete ? 148 : 184,
    endFrequency: isDelete ? 88 : 108,
    volume: isDelete ? 0.022 : 0.018,
    duration: 0.055,
  })
}

function MatrixRain({ className = '' }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (!canvas || !context || reducedMotion) return undefined

    let animationFrame = 0
    let width = 0
    let height = 0
    let fontSize = 16
    let drops = []
    let speeds = []

    const resize = () => {
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
      width = window.innerWidth
      height = window.innerHeight
      fontSize = width <= 720 ? 13 : 16

      canvas.width = Math.floor(width * pixelRatio)
      canvas.height = Math.floor(height * pixelRatio)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)

      const columnCount = Math.ceil(width / fontSize)
      drops = Array.from({ length: columnCount }, () => -Math.random() * (height / fontSize))
      speeds = Array.from({ length: columnCount }, () => 0.7 + Math.random() * 1.3)
      context.fillStyle = '#020805'
      context.fillRect(0, 0, width, height)
    }

    const draw = () => {
      context.fillStyle = 'rgba(1, 10, 5, 0.105)'
      context.fillRect(0, 0, width, height)
      context.font = `${fontSize}px 'IBM Plex Mono', monospace`
      context.textAlign = 'center'

      for (let column = 0; column < drops.length; column += 1) {
        const glyph = matrixGlyphs[Math.floor(Math.random() * matrixGlyphs.length)]
        const x = column * fontSize + fontSize / 2
        const y = drops[column] * fontSize
        const isHead = Math.random() > 0.94

        context.fillStyle = isHead ? 'rgba(208, 255, 218, 0.95)' : 'rgba(36, 255, 104, 0.62)'
        context.shadowBlur = isHead ? 10 : 0
        context.shadowColor = '#5aff7f'
        context.fillText(glyph, x, y)

        if (y > height + fontSize && Math.random() > 0.965) {
          drops[column] = -Math.random() * 18
          speeds[column] = 0.7 + Math.random() * 1.3
        } else {
          drops[column] += speeds[column]
        }
      }

      context.shadowBlur = 0
      animationFrame = window.requestAnimationFrame(draw)
    }

    resize()
    window.addEventListener('resize', resize, { passive: true })
    animationFrame = window.requestAnimationFrame(draw)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return <canvas ref={canvasRef} className={`matrix-rain ${className}`.trim()} aria-hidden="true" />
}

function TerminalIntro({ onComplete }) {
  const [line, setLine] = useState('')
  const [phase, setPhase] = useState('idle')
  const [started, setStarted] = useState(false)
  const audioRef = useRef(null)

  useEffect(() => () => {
    if (audioRef.current?.context.state !== 'closed') {
      audioRef.current?.context.close()
    }
  }, [])

  const startIntro = async () => {
    if (started) return

    const audio = createKeyboardAudio()
    audioRef.current = audio

    if (audio?.context.state === 'suspended') {
      await audio.context.resume().catch(() => undefined)
    }

    setLine('')
    setPhase('typing')
    setStarted(true)
  }

  useEffect(() => {
    if (!started) return undefined

    let cancelled = false
    let buffer = ''

    const pause = async (duration) => {
      await wait(duration)
      return !cancelled
    }

    const type = async (value, speed = 83) => {
      for (const character of value) {
        if (!await pause(speed + Math.random() * 48)) return false
        buffer += character
        setLine(buffer)
        playKeyboardSound(audioRef.current, 'type')
      }
      return true
    }

    const erase = async (amount, speed = 55) => {
      for (let index = 0; index < amount; index += 1) {
        if (!await pause(speed)) return false
        buffer = buffer.slice(0, -1)
        setLine(buffer)
        playKeyboardSound(audioRef.current, 'delete')
      }
      return true
    }

    const run = async () => {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      if (reducedMotion) {
        setLine('[<Z>]')
        if (!await pause(350)) return
        setPhase('exit')
        if (await pause(175)) onComplete()
        return
      }

      setLine('')
      setPhase('typing')

      if (!await type('{<', 93)) return
      if (!await pause(340)) return
      if (!await erase(2, 63)) return
      if (!await pause(130)) return

      if (!await type('[<', 93)) return
      if (!await pause(170)) return
      if (!await type('Z', 93)) return
      if (!await pause(210)) return
      if (!await type('>}', 93)) return
      if (!await pause(380)) return
      if (!await erase(1, 73)) return
      if (!await pause(150)) return
      if (!await type(']', 93)) return
      if (!await pause(410)) return

      setPhase('enter')
      playKeyboardSound(audioRef.current, 'enter')
      if (!await pause(310)) return
      setPhase('exit')
      if (await pause(450)) onComplete()
    }

    run()

    return () => {
      cancelled = true
    }
  }, [onComplete, started])

  return (
    <section className="terminal-intro" data-phase={phase} aria-label="Анимация логотипа KZK">
      <MatrixRain />
      <div className="terminal-scanlines" aria-hidden="true" />
      <div className="terminal-vignette" aria-hidden="true" />
      {started
        ? (
          <div className="terminal-command" aria-hidden="true">
            <span className="terminal-line">{line}</span>
            <span className="terminal-cursor" />
          </div>
        )
        : (
          <button type="button" className="terminal-start" onClick={startIntro} autoFocus>
            <span aria-hidden="true">&gt;</span>
            НАЖМИТЕ ENTER
          </button>
        )}
      <span className="visually-hidden">K, Z и зеркальная K</span>
    </section>
  )
}

const services = [
  {
    id: 'sites',
    number: '01',
    label: 'Сайты',
    title: 'Сайты',
    consoleLabel: 'WEB.DEV.CONSOLE',
    command: 'Создать сайт, который продаёт',
    description: 'Уникальный дизайн, быстрая разработка и запуск под ключ.',
    mobileDescription: 'Дизайн и запуск под ключ.',
    cta: 'Открыть веб-разработку',
    mobileCta: 'Сайты',
    color: '#69ff8a',
    delay: 0,
  },
  {
    id: 'emails',
    number: '02',
    label: 'HTML-письма',
    title: 'HTML-письма',
    consoleLabel: 'EMAIL.CONSOLE',
    command: 'Запустить письмо, которое читают',
    mobileCommand: 'Письмо, которое читают',
    description: 'Адаптивная HTML-вёрстка, которая выглядит идеально в каждой почте.',
    mobileDescription: 'Адаптивно для каждой почты.',
    cta: 'Открыть HTML-письма',
    mobileCta: 'HTML-письма',
    color: '#ea4335',
    delay: 120,
  },
  {
    id: 'bots',
    number: '03',
    label: 'Telegram-боты',
    title: 'Telegram-боты',
    consoleLabel: 'TELEGRAM.BOT.CONSOLE',
    command: 'Автоматизировать работу в Telegram',
    mobileCommand: 'Автоматизировать Telegram',
    description: 'Бот отвечает, продаёт и экономит ваше время каждый день.',
    mobileDescription: 'Бот продаёт и экономит время.',
    cta: 'Открыть Telegram-ботов',
    mobileCta: 'Telegram-боты',
    color: '#2aabee',
    delay: 240,
  },
]

function useTypedCommand(text, active, delay = 0) {
  const [typedText, setTypedText] = useState('')

  useEffect(() => {
    if (!active) {
      setTypedText('')
      return undefined
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setTypedText(text)
      return undefined
    }

    let cancelled = false
    let characterIndex = 0
    let timerId = 0

    const typeNextCharacter = () => {
      if (cancelled) return
      characterIndex += 1
      setTypedText(text.slice(0, characterIndex))
      if (characterIndex < text.length) {
        timerId = window.setTimeout(typeNextCharacter, 46 + Math.random() * 24)
      }
    }

    timerId = window.setTimeout(typeNextCharacter, delay)

    return () => {
      cancelled = true
      window.clearTimeout(timerId)
    }
  }, [active, delay, text])

  return typedText
}

function ConsoleWindowBar({ label }) {
  return (
    <div className="console-window-bar" aria-hidden="true">
      <span>{label}</span>
      <span className="console-window-controls">
        <Minus />
        <Square />
        <X />
      </span>
    </div>
  )
}

function WebConsolePreview() {
  return (
    <div className="console-preview web-console-preview" aria-hidden="true">
      <div className="browser-address">
        <GlobeHemisphereWest />
        <span>https://kazak.v/your-site</span>
      </div>
      <div className="web-editor">
        <div className="web-files">
          <span className="is-active"><Code /> index.html</span>
          <span>style.css</span>
          <span>app.js</span>
        </div>
        <div className="web-code">
          <span><i>01</i>&lt;main class="result"&gt;</span>
          <span><i>02</i>&nbsp;&nbsp;&lt;h1&gt;Ваш сайт&lt;/h1&gt;</span>
          <span><i>03</i>&nbsp;&nbsp;&lt;button&gt;Заказать&lt;/button&gt;</span>
          <span><i>04</i>&lt;/main&gt;</span>
        </div>
      </div>
    </div>
  )
}

function EmailConsolePreview() {
  return (
    <div className="console-preview email-console-preview" aria-hidden="true">
      <div className="mail-toolbar">
        <span className="mail-icon"><EnvelopeSimple weight="fill" /></span>
        <span>Новое письмо</span>
        <span className="mail-toolbar-dots">
          <i />
          <i />
          <i />
          <i />
        </span>
      </div>
      <div className="mail-field">
        <span>Кому:</span>
        <strong>ваш.клиент@inbox.ru</strong>
      </div>
      <div className="mail-field mail-subject">
        <span>Тема:</span>
        <strong>Предложение, которое хочется открыть</strong>
      </div>
      <p>Ваше предложение будет выглядеть идеально на любом экране.</p>
    </div>
  )
}

function BotConsolePreview() {
  return (
    <div className="console-preview bot-console-preview" aria-hidden="true">
      <div className="bot-profile">
        <span className="telegram-mark"><PaperPlaneTilt weight="fill" /></span>
        <span><strong>@kazak_web_bot</strong><small>бот</small></span>
      </div>
      <div className="bot-message">
        <span>/start</span>
        <strong>Готово. Беру рутину на себя.</strong>
      </div>
    </div>
  )
}

function ServicePreview({ serviceId }) {
  if (serviceId === 'sites') return <WebConsolePreview />
  if (serviceId === 'emails') return <EmailConsolePreview />
  return <BotConsolePreview />
}

function getServiceFromUrl() {
  const serviceId = new URLSearchParams(window.location.search).get('service')
  return services.some((service) => service.id === serviceId) ? serviceId : null
}

function ServiceConsole({ service, onOpen, isVisible }) {
  const desktopCommand = useTypedCommand(service.command, isVisible, service.delay)
  const mobileCommand = useTypedCommand(service.mobileCommand ?? service.command, isVisible, service.delay)
  const isComplete = desktopCommand.length === service.command.length

  return (
    <button
      type="button"
      className={`service-console service-console-${service.id}`}
      style={{ '--service-accent': service.color }}
      onClick={() => onOpen(service.id)}
      aria-label={`${service.command}. ${service.description} ${service.cta}`}
    >
      <ConsoleWindowBar label={service.consoleLabel} />
      <ServicePreview serviceId={service.id} />
      <span className="service-console-copy">
        <strong className="console-command console-command-desktop" aria-hidden="true">
          <span>&gt;&nbsp;</span>{desktopCommand}<i className={isComplete ? 'is-complete' : ''} />
        </strong>
        <strong className="console-command console-command-mobile" aria-hidden="true">
          <span>&gt;&nbsp;</span>{mobileCommand}<i className={mobileCommand.length === (service.mobileCommand ?? service.command).length ? 'is-complete' : ''} />
        </strong>
        <span className="console-description console-description-desktop">{service.description}</span>
        <span className="console-description console-description-mobile">{service.mobileDescription}</span>
        <span className="console-cta console-cta-desktop">{service.cta}<ArrowRight weight="bold" /></span>
        <span className="console-cta console-cta-mobile">{service.mobileCta}<ArrowRight weight="bold" /></span>
      </span>
    </button>
  )
}

function ServiceGateway({ onOpen, isVisible }) {
  return (
    <main className="service-gateway" aria-label="Студия веб-разработки Kazak.V">
      {isVisible ? <MatrixRain className="gateway-matrix" /> : null}
      <header className="studio-brand">
        <img src="/assets/kazak-z-logo.png" alt="Логотип Kazak.V: [<Z>]" />
        <h1>Студия веб-разработки <span>Kazak.V</span></h1>
      </header>

      <section className="service-console-grid" aria-label="Выберите услугу">
        {services.map((service) => (
          <ServiceConsole
            key={service.id}
            service={service}
            onOpen={onOpen}
            isVisible={isVisible}
          />
        ))}
      </section>

      <ContactLinks variant="gateway" />
    </main>
  )
}

export default function App() {
  const [activeServiceId, setActiveServiceId] = useState(getServiceFromUrl)
  const [showIntro, setShowIntro] = useState(() => getServiceFromUrl() === null)
  const activeService = services.find((service) => service.id === activeServiceId) ?? null

  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.has('style')) {
      url.searchParams.delete('style')
      window.history.replaceState({}, '', url)
    }

    const handlePopState = () => setActiveServiceId(getServiceFromUrl())
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const openService = (serviceId) => {
    const url = new URL(window.location.href)
    url.searchParams.set('service', serviceId)
    url.searchParams.delete('style')
    window.history.pushState({}, '', url)
    setActiveServiceId(serviceId)
  }

  const closeService = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('service')
    window.history.pushState({}, '', url)
    setActiveServiceId(null)
  }

  return (
    <>
      {activeService
        ? <ServicePage service={activeService} onBack={closeService} onNavigate={openService} />
        : <ServiceGateway onOpen={openService} isVisible={!showIntro} />}
      {showIntro
        ? <TerminalIntro onComplete={() => setShowIntro(false)} />
        : null}
    </>
  )
}
